/**
 * Sitemap generator for Emplois BNSSA & MNS.
 *
 * Builds a Google-compliant <urlset> XML document from:
 *   1. A canonical list of static, public routes (declared below).
 *   2. The merged SEO pages (static `seoPages.ts` + Lumi `seo_pages` overrides/customs),
 *      filtering out pages where `isActive === false`.
 *
 * Excluded by design:
 *   - Dynamic routes with params (`:id`, `:jobSlug`, `:childSlug`).
 *   - Pages handled as 301-equivalent redirects (`/offre-emploi`, `/offre/:id`,
 *     `/disponibles-immediatement`, `/confidentialite`, `/cgu`, `/seo/:slug`).
 *   - Private/auth-restricted areas (`/dashboard`, `/backoffice`, `/dashboard-organisme`, `/admin`).
 *
 * The XML returned is identical to what should be served at `https://bnssa-mns.fr/sitemap.xml`.
 */
import type { SEOPage } from '@/data/seoPages'

export const SITE_BASE_URL = 'https://bnssa-mns.fr'

export type ChangeFreq =
  | 'always'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'yearly'
  | 'never'

export interface SitemapEntry {
  /** Path part only, e.g. `/bnssa/offres-emploi`. Will be prefixed with `SITE_BASE_URL`. */
  path: string
  changefreq?: ChangeFreq
  priority?: number
  /** ISO 8601 date string; if omitted, no `<lastmod>` is emitted. */
  lastmod?: string
}

/**
 * Canonical static routes. Order is preserved in the XML output.
 * Only includes URLs that are publicly indexable AND canonical
 * (i.e. not behind a 301-equivalent redirect, not private).
 */
const STATIC_ENTRIES: SitemapEntry[] = [
  // ── Accueil ──
  { path: '/', changefreq: 'daily', priority: 1.0 },

  // ── Offres d'emploi (canonique) ──
  { path: '/offres', changefreq: 'daily', priority: 0.9 },

  // ── Formations (catalogue global) ──
  { path: '/formations', changefreq: 'weekly', priority: 0.8 },

  // ── CVthèque (canonique) ──
  { path: '/disponibles', changefreq: 'daily', priority: 0.85 },

  // ── FAQ ──
  { path: '/faq', changefreq: 'monthly', priority: 0.6 },

  // ── Auth ──
  { path: '/inscription', changefreq: 'monthly', priority: 0.5 },
  { path: '/connexion', changefreq: 'monthly', priority: 0.4 },

  // ── Pages légales (canoniques uniquement) ──
  { path: '/mentions-legales', changefreq: 'yearly', priority: 0.3 },
  { path: '/politique-confidentialite', changefreq: 'yearly', priority: 0.3 },
  { path: '/cgv', changefreq: 'yearly', priority: 0.3 },
]

/**
 * Heuristic to assign changefreq/priority to a SEO page based on its role.
 */
function inferSeoFreqPriority(page: SEOPage): { changefreq: ChangeFreq; priority: number } {
  const path = page.path
  // Pillars (silo home pages)
  if (page.isPillar) return { changefreq: 'weekly', priority: 0.95 }
  // Job listings within a silo
  if (path.endsWith('/offres-emploi')) return { changefreq: 'daily', priority: 0.9 }
  // Formation listings within a silo
  if (path.endsWith('/formations')) return { changefreq: 'weekly', priority: 0.85 }
  // Default: long-form silo guides
  return { changefreq: 'monthly', priority: 0.75 }
}

/**
 * Convert merged SEO pages into sitemap entries, excluding inactive pages.
 * Inactive = `isActive === false` (undefined/true are kept).
 */
export function seoPagesToSitemapEntries(pages: SEOPage[]): SitemapEntry[] {
  return pages
    .filter((p) => p.isActive !== false)
    .filter((p) => typeof p.path === 'string' && p.path.startsWith('/'))
    .map((p) => {
      const fp = inferSeoFreqPriority(p)
      return {
        path: p.path,
        changefreq: fp.changefreq,
        priority: fp.priority,
      }
    })
}

/**
 * Merge static + dynamic entries, deduplicate by path (first wins),
 * and sort: home first, then by priority desc, then alphabetical.
 */
export function buildSitemapEntries(seoPages: SEOPage[]): SitemapEntry[] {
  const seoEntries = seoPagesToSitemapEntries(seoPages)
  const seen = new Set<string>()
  const merged: SitemapEntry[] = []

  for (const entry of [...STATIC_ENTRIES, ...seoEntries]) {
    if (seen.has(entry.path)) continue
    seen.add(entry.path)
    merged.push(entry)
  }

  return merged.sort((a, b) => {
    if (a.path === '/') return -1
    if (b.path === '/') return 1
    const pa = a.priority ?? 0.5
    const pb = b.priority ?? 0.5
    if (pa !== pb) return pb - pa
    return a.path.localeCompare(b.path)
  })
}

/** Escape XML special chars in URLs (just in case). */
function xmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

/**
 * Render a list of entries as a Google-compliant sitemap XML document.
 * Includes a generation comment with timestamp for traceability.
 */
export function renderSitemapXml(entries: SitemapEntry[]): string {
  const generatedAt = new Date().toISOString()
  const urls = entries
    .map((e) => {
      const loc = `${SITE_BASE_URL}${e.path}`
      const parts = [
        '  <url>',
        `    <loc>${xmlEscape(loc)}</loc>`,
      ]
      if (e.lastmod) parts.push(`    <lastmod>${xmlEscape(e.lastmod)}</lastmod>`)
      if (e.changefreq) parts.push(`    <changefreq>${e.changefreq}</changefreq>`)
      if (typeof e.priority === 'number') {
        parts.push(`    <priority>${e.priority.toFixed(2)}</priority>`)
      }
      parts.push('  </url>')
      return parts.join('\n')
    })
    .join('\n')

  return `<?xml version="1.0" encoding="UTF-8"?>
<!-- Sitemap auto-généré pour Emplois BNSSA & MNS le ${generatedAt} -->
<!-- Ne pas éditer manuellement : régénérer depuis l'admin (onglet "Sitemap"). -->
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls}
</urlset>
`
}

/** One-shot helper combining build + render. */
export function generateSitemapXml(seoPages: SEOPage[]): string {
  return renderSitemapXml(buildSitemapEntries(seoPages))
}
